<template>
  <b-modal
    :visible="show"
    @change="val => $emit('update:show', val)"
    title="Import Items"
    size="lg"
    hide-footer
    @hidden="resetModal"
    no-close-on-backdrop
    no-close-on-esc
  >
    <div class="import-modal-container">
      <!-- Upload Section -->
      <div v-if="!fileProcessing && !previewData.length" class="upload-section">
        <div class="mb-3 d-flex justify-content-between align-items-center">
          <div>
            <h5 class="mb-1">Upload Items Excel File</h5>
            <p class="text-muted small mb-0">Upload your Excel file to bulk import items.</p>
          </div>
          <b-button variant="outline-primary" size="sm" @click="downloadTemplate">
            <feather-icon icon="DownloadIcon" class="mr-50" />
            Download Excel Template
          </b-button>
        </div>

        <b-form-file
          v-model="file"
          placeholder="Choose a file or drop it here..."
          drop-placeholder="Drop file here..."
          accept=".xlsx, .xls, .csv"
          class="mb-3"
          :disabled="processing"
        >
          <template slot="file-name" slot-scope="{ names }">
            <b-badge variant="dark">{{ names[0] }}</b-badge>
          </template>
        </b-form-file>
      </div>

      <!-- Processing State -->
      <div v-if="fileProcessing" class="text-center py-5">
        <b-spinner variant="primary" class="mb-2" />
        <h5 class="text-primary">Processing Excel File...</h5>
        <p class="text-muted small">Please wait while we read and validate your data.</p>
      </div>

      <!-- Warning/Error Messages -->
      <b-alert
        v-if="validationErrors.length > 0 && !fileProcessing"
        show
        variant="danger"
        class="mt-2 mb-3"
      >
        <div class="d-flex align-items-center mb-1">
          <feather-icon icon="AlertCircleIcon" size="18" class="mr-50" />
          <h6 class="mb-0 text-danger font-weight-bolder">Validation Errors Found</h6>
        </div>
        <ul class="mb-0 pl-3">
          <li v-for="(error, index) in validationErrors" :key="index" class="small">
            {{ error }}
          </li>
        </ul>
      </b-alert>

      <!-- Preview Section -->
      <div v-if="previewData.length > 0 && !fileProcessing" class="preview-section mt-3">
        <div class="d-flex justify-content-between align-items-center mb-2">
          <h5 class="mb-0">Data Preview</h5>
          <b-badge variant="light-primary" pill>
            {{ previewData.length }} Items Found
          </b-badge>
        </div>

        <div class="table-responsive bg-white rounded border">
          <b-table
            :items="previewData"
            :fields="previewFields"
            hover
            small
            striped
            responsive
            class="mb-0 custom-preview-table"
            head-variant="light"
            sticky-header="300px"
          >
            <!-- Cell Overrides for better formatting -->
            <template #cell(itemPurchaseRate)="data">
              {{ formatCurrency(data.value) }}
            </template>
            <template #cell(tradePrice)="data">
              {{ formatCurrency(data.value) }}
            </template>
            <template #cell(retailPrice)="data">
              {{ formatCurrency(data.value) }}
            </template>
            <template #cell(salesTaxPercent)="data">
              {{ data.value }}%
            </template>
            <template #cell(addSalesTaxPercent)="data">
              {{ data.value }}%
            </template>
          </b-table>
        </div>

        <!-- Progress Bar during upload -->
        <div v-if="processing" class="mt-3">
          <div class="d-flex justify-content-between mb-50">
            <small class="font-weight-semibold">{{ progressText }}</small>
            <small class="font-weight-semibold">{{ progressValue }}%</small>
          </div>
          <b-progress :value="progressValue" max="100" variant="primary" animated class="progress-bar-sm" />
        </div>

        <!-- Start Uploading Action -->
        <div class="mt-4 d-flex justify-content-end" v-if="validationErrors.length === 0">
          <b-button variant="outline-secondary" class="mr-2" @click="resetFile" :disabled="processing">
            Cancel
          </b-button>
          <b-button variant="primary" @click="onSubmit" :disabled="processing">
            <b-spinner v-if="processing" small class="mr-50" />
            <feather-icon v-else icon="UploadIcon" class="mr-50" />
            {{ processing ? 'Importing...' : 'Start Import' }}
          </b-button>
        </div>
        <!-- Retrying Action Process -->
        <div class="mt-4 d-flex justify-content-end" v-if="validationErrors.length > 0">
          <b-button variant="outline-danger" class="mr-2" @click="resetFile" :disabled="processing">
            Discard File
          </b-button>
        </div>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { ref, computed, watch } from '@vue/composition-api';
import {
  BModal,
  BFormFile,
  BAlert,
  BTable,
  BProgress,
  BBadge,
  BSpinner,
  BButton,
} from 'bootstrap-vue';
import FeatherIcon from '@core/components/feather-icon/FeatherIcon.vue';
import * as XLSX from 'xlsx';

export default {
  name: 'ItemsListImportModal',
  components: {
    BModal,
    BFormFile,
    BAlert,
    BTable,
    BProgress,
    FeatherIcon,
    BBadge,
    BSpinner,
    BButton,
  },
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['bulkImport', 'update:show'],
  setup(props, { emit }) {
    const file = ref(null);
    const processing = ref(false);
    const fileProcessing = ref(false);
    const validationErrors = ref([]);
    const previewData = ref([]);
    const progressValue = ref(0);
    const progressText = ref('');

    const downloadTemplate = () => {
      // Define headers matching the API requirements
      const headers = [
        'Item Code*',
        'Item Name*',
        'Unit ID*',
        'Purchase Rate*',
        'Trade Price*',
        'Retail Price*',
        'Sales Tax %*',
        'Additional Sales Tax %',
        'HS Code ID*'
      ];
      
      const sampleRow = [
        'ITM-001',
        'Gaming Keyboard',
        1,
        1500.00,
        1800.00,
        2000.00,
        18.00,
        3.00,
        5
      ];
      
      const sampleRow2 = [
        'ITM-002',
        'Wireless Mouse',
        1,
        800.00,
        1000.00,
        1200.00,
        18.00,
        3.00,
        5
      ];

      const wb = XLSX.utils.book_new();
      const wsData = [headers, sampleRow, sampleRow2];
      const ws = XLSX.utils.aoa_to_sheet(wsData);

      const colWidths = [
          { wch: 20 }, // Item Code
          { wch: 30 }, // Item Name
          { wch: 15 }, // Unit ID
          { wch: 20 }, // Purchase Rate
          { wch: 20 }, // Trade Price
          { wch: 20 }, // Retail Price
          { wch: 15 }, // Sales Tax %
          { wch: 25 }, // Additional Sales Tax %
          { wch: 15 }, // HS Code ID
      ];
      ws['!cols'] = colWidths;

      XLSX.utils.book_append_sheet(wb, ws, 'Items');
      XLSX.writeFile(wb, 'Items_BulkUpload_Template.xlsx');
    };

    const previewFields = [
      { key: 'itemCode', label: 'Item Code', sortable: true },
      { key: 'itemName', label: 'Item Name', sortable: true },
      { key: 'unitID', label: 'Unit ID', sortable: true },
      { key: 'itemPurchaseRate', label: 'Purchase Rate', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
      { key: 'tradePrice', label: 'Trade Price', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
      { key: 'retailPrice', label: 'Retail Price', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
      { key: 'salesTaxPercent', label: 'Sales Tax', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
      { key: 'addSalesTaxPercent', label: 'Add. Tax', sortable: true, thClass: 'text-right', tdClass: 'text-right' },
      { key: 'itemHSCodeID', label: 'HS Code ID', sortable: true },
    ];

    watch(file, async (newFile) => {
      if (newFile) {
        fileProcessing.value = true;
        validationErrors.value = [];
        previewData.value = [];

        try {
          await processExcelFile(newFile);
        } finally {
          fileProcessing.value = false;
        }
      } else {
        previewData.value = [];
        validationErrors.value = [];
      }
    });

    const processExcelFile = async (excelFile) => {
      try {
        const data = await readExcelFile(excelFile);

        if (!data || data.length === 0) {
          validationErrors.value = ['Excel file appears to be empty or invalid.'];
          return;
        }

        const { items, errors } = parseExcelData(data);

        validationErrors.value = errors;
        previewData.value = items;
      } catch (error) {
        console.error('Error processing Excel file:', error);
        validationErrors.value = [
          "Error reading Excel file. Please ensure it's a valid Excel file.",
          'Make sure the file follows the template format exactly.',
        ];
      }
    };

    const readExcelFile = (file) => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = (e) => {
          try {
            const data = new Uint8Array(e.target.result);
            const workbook = XLSX.read(data, {
              type: 'array',
              cellDates: true,
              cellNF: false,
              cellText: false,
            });

            const sheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[sheetName];

            const jsonData = XLSX.utils.sheet_to_json(worksheet, {
              header: 1,
              raw: true, // Keep raw native numbers and avoid Excel auto-string formatting
              dateNF: 'yyyy-mm-dd',
            });

            resolve(jsonData);
          } catch (error) {
            reject(error);
          }
        };
        reader.onerror = reject;
        reader.readAsArrayBuffer(file);
      });
    };

    const parseExcelData = (excelData) => {
      if (excelData.length < 2) {
        return {
          items: [],
          errors: ['Excel file must contain at least a header row and one data row.'],
        };
      }

      const headers = excelData[0];
      const dataRows = excelData.slice(1);
      const errors = [];
      const items = [];

      const requiredColumns = {
        itemCode: headers.findIndex((h) => h && h.toString().includes('Item Code')),
        itemName: headers.findIndex((h) => h && h.toString().includes('Item Name')),
        unitID: headers.findIndex((h) => h && h.toString().includes('Unit ID')),
        itemPurchaseRate: headers.findIndex((h) => h && h.toString().includes('Purchase Rate')),
        tradePrice: headers.findIndex((h) => h && h.toString().includes('Trade Price')),
        retailPrice: headers.findIndex((h) => h && h.toString().includes('Retail Price')),
        salesTaxPercent: headers.findIndex((h) => h && h.toString().includes('Sales Tax %')),
        addSalesTaxPercent: headers.findIndex((h) => h && h.toString().includes('Additional Sales Tax %')),
        itemHSCodeID: headers.findIndex((h) => h && h.toString().includes('HS Code ID')),
      };

      const missingColumns = Object.entries(requiredColumns)
        .filter(([key, index]) => index === -1 && key !== 'addSalesTaxPercent') // Allow AddSalesTax as optional if missing totally
        .map(([key]) => key);

      // Relax column validation lightly if names slightly vary, as long as many match.
      // E.g., assume strict match for crucial ones:
      if (requiredColumns.itemCode === -1 || requiredColumns.itemName === -1) {
        errors.push(`Missing required columns: Item Code, Item Name`);
        errors.push('Please ensure your Excel file follows the template format exactly.');
        return { items: [], errors };
      }

      dataRows.forEach((row, rowIndex) => {
        if (row.length === 0 || !row.some((cell) => cell !== null && cell !== undefined && cell !== '')) {
          return;
        }

        const itemCode = row[requiredColumns.itemCode];
        if (!itemCode) {
          errors.push(`Row ${rowIndex + 2}: Item Code is required`);
          return;
        }

        const itemName = row[requiredColumns.itemName];
        if (!itemName) {
          errors.push(`Row ${rowIndex + 2}: Item Name is required`);
          return;
        }

        // Safe parse integer values
        const safelyParseInt = (val) => {
            if (val === null || val === undefined || val === '') return NaN;
            if (typeof val === 'number') return Math.floor(val);
            return parseInt(val.toString().replace(/,/g, ''), 10);
        };

        const unitID = safelyParseInt(row[requiredColumns.unitID]);
        if (isNaN(unitID)) {
          errors.push(`Row ${rowIndex + 2}: Unit ID must be a valid number`);
          return;
        }

        // Safe parse floats
        const safelyParseValue = (val) => {
            if (val === null || val === undefined || val === '') return 0;
            if (typeof val === 'number') return val;
            const parsed = parseFloat(val.toString().replace(/,/g, ''));
            return isNaN(parsed) ? 0 : parsed;
        };

        const parsedHSCode = safelyParseInt(row[requiredColumns.itemHSCodeID]);

        const item = {
          itemCode: itemCode.toString().trim(),
          itemName: itemName.toString().trim(),
          unitID: unitID,
          itemGroupID: 0,
          itemPurchaseRate: safelyParseValue(row[requiredColumns.itemPurchaseRate]),
          tradePrice: safelyParseValue(row[requiredColumns.tradePrice]),
          retailPrice: safelyParseValue(row[requiredColumns.retailPrice]),
          salesTaxPercent: requiredColumns.salesTaxPercent !== -1 ? safelyParseValue(row[requiredColumns.salesTaxPercent]) : 0,
          addSalesTaxPercent: requiredColumns.addSalesTaxPercent !== -1 ? safelyParseValue(row[requiredColumns.addSalesTaxPercent]) : 0,
          itemHSCodeID: isNaN(parsedHSCode) ? 0 : parsedHSCode
        };

        items.push(item);
      });

      if (items.length === 0 && errors.length === 0) {
        errors.push('No valid items found in the Excel file');
      }

      return { items, errors };
    };

    const formatCurrency = (value) => {
      if (value === null || value === undefined) return '0.00';
      return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(value);
    };

    const onSubmit = async () => {
      if (!file.value || previewData.value.length === 0) {
        return;
      }

      processing.value = true;
      progressValue.value = 50;
      progressText.value = 'Uploading items to server...';

      // emit allows parent to perform identical store.dispatch logic as InvoiceList
      emit('bulkImport', previewData.value, (success, response) => {
        const responseData = response?.data || {};
        const innerData = responseData?.data || {};
        const hasErrors = innerData?.errors && innerData.errors.length > 0;

        if (success && responseData.status !== 'error' && !hasErrors) {
          progressValue.value = 100;
          progressText.value = 'Import completed successfully!';

          setTimeout(() => {
            resetModal();
            emit('update:show', false);
          }, 1500);
        } else {
          let errs = [];
          if (hasErrors) {
            errs = innerData.errors;
          } else if (responseData.message) {
            errs = [responseData.message];
          } else if (typeof response === 'string') {
            errs = [response];
          } else if (response?.message) {
            errs = [response.message];
          } else {
            errs = ['Unknown error occurred while interacting with the server.'];
          }

          validationErrors.value = errs;
          progressValue.value = 0;
          progressText.value = 'Import completed with errors.';
        }
        processing.value = false;
      });
    };

    const resetFile = () => {
      file.value = null;
    };

    const resetModal = () => {
      file.value = null;
      processing.value = false;
      fileProcessing.value = false;
      validationErrors.value = [];
      previewData.value = [];
      progressValue.value = 0;
      progressText.value = '';
    };

    return {
      file,
      processing,
      fileProcessing,
      validationErrors,
      previewData,
      previewFields,
      progressValue,
      progressText,
      downloadTemplate,
      onSubmit,
      resetFile,
      resetModal,
      formatCurrency,
    };
  },
};
</script>

<style lang="scss" scoped>
.import-modal-container {
  padding: 1rem;
}

.custom-preview-table {
  font-size: 0.85rem;
  
  th {
    white-space: nowrap;
    background-color: #f8f8f8;
  }
}

.b-table-sticky-header {
  margin-bottom: 0;
}
</style>
